import { Tooltip, Typography, Button, Box, CircularProgress } from '@mui/material';
import Dialog from 'components/dialog';
import { Trash2 } from 'lucide-react';
import Mui from 'materials';
import React, { PropsWithChildren, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import titleCase from 'utils/title-case';
import { useCreateCalibration } from 'views/bluviu-nutrition/features/calibrations/hooks/use-create-calibration';
import { CalibrationModel } from 'views/bluviu-nutrition/features/calibrations/types/calibration-model.enum';
import { CalibrationState } from 'views/bluviu-nutrition/features/calibrations/types/calibration-state.enum';
import { CalibrationsParams } from 'views/bluviu-nutrition/features/calibrations/types/calibrations-params.type';
import { useSelectedSite } from 'views/bluviu-nutrition/hooks/use-selected-site';

type Props = PropsWithChildren & {
  button?: React.ReactElement;
  calibration: {
    name: string;
    id: string;
    model: CalibrationModel;
    state: CalibrationState;
  };
};

const DeleteCalibrationDialog: React.FC<Props> = ({ calibration, button }) => {
  const { selectedSite } = useSelectedSite();
  const { deleteCalibration } = useCreateCalibration();
  const queryClient = useQueryClient();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const params = useParams<CalibrationsParams>();

  const onSubmit = () => {
    deleteCalibration.mutate(
      { model: calibration.model, calibrationId: calibration.id },
      {
        onSuccess: () => {
          dialogRef.current?.setOpen(false);
          queryClient.invalidateQueries({
            queryKey: ['getCalibrations', params.globalSiteId]
          });
        },
      }
    );
  };

  return (
    <Dialog
      ref={dialogRef}
      maxWidth={'sm'}
      title={`Delete calibration (${titleCase(calibration.name)})`}
      button={
        button || (
          <Tooltip title={'Delete'}>
            <Mui.IconButton disabled={calibration.state === CalibrationState.SUBMITTED}>
              <Trash2 size={22} />
            </Mui.IconButton>
          </Tooltip>
        )
      }>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography fontSize={14}>From site: {selectedSite?.name}</Typography>
        <Box>
          <FormattedMessage id="general.deleteComment" />
        </Box>
        <Box display={'flex'} justifyContent={'end'} gap={4}>
          <Button variant="outlined" type="submit" size="small" onClick={() => dialogRef.current?.close()}>
            <FormattedMessage id="general.cancel" />
          </Button>
          <Button
            onClick={() => onSubmit()}
            disabled={deleteCalibration.isPending}
            startIcon={deleteCalibration.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
            Delete
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default React.memo(DeleteCalibrationDialog);
